import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import {
  rem,
  toVWDesktop,
  vwDesktop,
  vwMobile,
} from "../../helpers/styles";
import { MEDIA_DESKTOP } from "../../helpers/theme";
import MailingList from "../MailingList";
import SignUpModalFormV2 from "./SignUpModalFormV2";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import {
  openSignUpModal,
  closeSignUpModal,
} from "../../store/modals";
import { useAriaHiddenTool } from "../../hooks/useAriaHiddenTool";
import { isUSLocale } from "../../helpers/locales";

export default function SignUpModal({
  hideInitially,
}: {
  hideInitially?: boolean;
}): JSX.Element | null {
  const signUpModal = useAppSelector(
    (state) => state.modals.signUp,
  );
  const dispatch = useAppDispatch();
  const backdropRef = useRef<HTMLDivElement>(null);
  const isModalHidden = !signUpModal?.show;
  const reskin = isUSLocale();

  useEffect(() => {
    const t = setTimeout(() => {
      if (
        signUpModal?.timesDisplayed === 0 &&
        !hideInitially
      ) {
        dispatch(openSignUpModal());
      }
    }, 3 * 1000);
    return () => clearTimeout(t);
  }, [signUpModal?.timesDisplayed, hideInitially]);

  useAriaHiddenTool(backdropRef, isModalHidden);

  return (
    <Backdrop
      role="dialog"
      ref={backdropRef}
      style={{ display: isModalHidden ? "none" : "flex" }}
      onClick={(e) =>
        e.target === e.currentTarget &&
        dispatch(closeSignUpModal())
      }
      aria-labelledby="mailingListModalTitle"
      aria-modal="true"
    >
      <Box role="document">
        {reskin ? (
          <SignUpModalFormV2 titleId="mailingListModalTitle" />
        ) : (
          <MailingList titleId="mailingListModalTitle" />
        )}
        <Close
          reskin={reskin}
          onClick={() => dispatch(closeSignUpModal())}
          aria-label="Close"
        >
          &times;
        </Close>
      </Box>
    </Backdrop>
  );
}

const Close = styled.button<{ reskin: boolean }>`
  position: absolute;
  top: ${(props) => (props.reskin ? `${rem(10)}` : `${vwMobile(10)}`)};
  right: ${(props) => (props.reskin ? `${rem(24)}` : `${vwMobile(10)}`)};
  width: ${vwMobile(30)};
  height: ${vwMobile(30)};

  font-size: ${rem(40)};
  line-height: 100%;
  color: ${(props) => (props.reskin ? "#f6f1e7" : "inherit")};

  background: transparent;
  border: none;
  cursor: pointer;

  ${MEDIA_DESKTOP} {
    top: ${(props) => (props.reskin ? `${rem(15)}` : `${vwMobile(10)}`)};
    right: ${(props) => (props.reskin ? `${rem(47)}` : `${vwMobile(10)}`)};
    width: ${vwDesktop(30)};
    height: ${vwDesktop(30)};
  }
`;

const Box = styled.div`
  width: 90%;
  position: relative;

  ${MEDIA_DESKTOP} {
    width: 50%;
    > section {
      padding: ${toVWDesktop(60)}vw;
    }
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  direction: ${(props) =>
    props.theme.isRTL ? "rtl" : "ltr"};

  background-color: rgba(0, 0, 0, 0.7);

  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
`;
